import api from './api';

// ============================================
// DynamicPageService – Port des Flutter DynamicPageService
// CMS-Seiten unter /dartcloud/:slug (z. B. Impressum, Datenschutz).
//   GET /api/pages/:slug  -> { data: DynamicPage }
//   GET /api/pages        -> { data: DynamicPage[] }  (sichtbare Seiten)
// ============================================

export type ContentBlockType = 'heading' | 'paragraph' | 'image' | 'divider' | 'list' | string;

export interface ContentBlock {
  id: string;
  type: ContentBlockType;
  text: string;
  level: number; // heading: 1|2|3
  imageUrl: string;
  imageAlt: string;
  imageCaption: string;
  imageMaxWidth?: number | null;
  listItems: string[];
  listType: 'bullet' | 'numbered' | string;
  order: number;
}

export interface DynamicPage {
  id: string;
  slug: string;
  title: string;
  metaDescription: string;
  contentBlocks: ContentBlock[];
  isVisible: boolean;
  showLastUpdated: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

const parseBlock = (b: any): ContentBlock => ({
  id: b?.blockId ?? b?._id ?? '',
  type: b?.type ?? 'paragraph',
  text: b?.text ?? '',
  level: b?.level ?? 2,
  imageUrl: b?.imageUrl ?? '',
  imageAlt: b?.imageAlt ?? '',
  imageCaption: b?.imageCaption ?? '',
  imageMaxWidth: typeof b?.imageMaxWidth === 'number' ? b.imageMaxWidth : null,
  listItems: Array.isArray(b?.listItems) ? b.listItems.filter((x: any) => typeof x === 'string') : [],
  listType: b?.listType ?? 'bullet',
  order: b?.order ?? 0,
});

const parsePage = (d: any): DynamicPage | null => {
  if (!d || typeof d !== 'object') return null;
  return {
    id: d._id ?? d.id ?? '',
    slug: d.slug ?? '',
    title: d.title ?? '',
    metaDescription: d.metaDescription ?? '',
    contentBlocks: Array.isArray(d.contentBlocks) ? d.contentBlocks.map(parseBlock) : [],
    isVisible: d.isVisible ?? true,
    showLastUpdated: d.showLastUpdated ?? true,
    createdAt: d.createdAt ?? null,
    updatedAt: d.updatedAt ?? null,
  };
};

class DynamicPageService {
  /** Öffentliche Seite per Slug. null wenn nicht gefunden. */
  async getPageBySlug(slug: string): Promise<DynamicPage | null> {
    try {
      const res = await api.get(`/api/pages/${encodeURIComponent(slug)}`);
      const data = res?.data?.data ?? res?.data;
      return parsePage(data);
    } catch {
      return null;
    }
  }

  /** Alle sichtbaren Seiten (z. B. für Navigation). */
  async getVisiblePages(): Promise<DynamicPage[]> {
    try {
      const res = await api.get('/api/pages');
      const data = res?.data?.data;
      if (!Array.isArray(data)) return [];
      return data.map(parsePage).filter((p): p is DynamicPage => p !== null);
    } catch {
      return [];
    }
  }

  /** Baut eine Bild-URL für einen ContentBlock robust. */
  buildImageUrl(url?: string | null): string | null {
    if (!url || !url.trim()) return null;
    const u = url.trim();
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    const base = api.defaults.baseURL || '';
    if (u.startsWith('/')) return `${base}${u}`;
    return `${base}/api/file/${u}`;
  }
}

export default new DynamicPageService();
